import { CUSTOMER_ID, KYC_WEB_DATA_CACHE, isClient, OPERATE_CUSTOMER_ID } from "@adgm/libs";
import type { TKycFormV2 } from "@adgm/cross-models";
import { EKycStep } from "@adgm/cross-models";

/**
 * KYC类型枚举
 */
export enum EKycType {
  INDIVIDUAL = "individual",
  INSTITUTION = "institution",
}

/**
 * KYC表单本地存储数据结构
 */
export interface IKycLocalStorage<T = TKycFormV2> {
  formData: T; // kyc缓存的数据，平铺在formData里面，前端需要用的时候直接拿或者从中提取
  [EKycType.INDIVIDUAL]: EKycStep; // 个人kyc认证到第几步
  [EKycType.INSTITUTION]: EKycStep; // 机构kyc认证到第几步
}

/**
 * KYC Web存储管理器
 */
export class KycWebStorage<T = TKycFormV2> {
  // 初始化的KYC存储数据结构
  private getDefaultForm(): IKycLocalStorage<T> {
    return {
      formData: {} as T,
      [EKycType.INDIVIDUAL]: EKycStep.QUESTIONNAIRE,
      [EKycType.INSTITUTION]: EKycStep.QUESTIONNAIRE,
    };
  }


  /**
   * 获取存储键
   */
  private getStorageKey(): string {
    const customerId = isClient ? localStorage.getItem(OPERATE_CUSTOMER_ID) || sessionStorage.getItem(CUSTOMER_ID) || "" : "";
    return `${customerId}-${KYC_WEB_DATA_CACHE}`;
  }

  /**
   * 从本地存储中加载数据
   */
  loadFromLocalStorage(): IKycLocalStorage<T> {
    if (!isClient) return this.getDefaultForm();

    try {
      const dataStr = localStorage.getItem(this.getStorageKey());
      if (!dataStr) return this.getDefaultForm();

      const data = JSON.parse(dataStr);
      return data as IKycLocalStorage<T>;
    } catch (error) {
      console.error("Failed to load KYC data from storage:", error);
      return this.getDefaultForm();
    }
  }

  /**
   * 清理kyc表单
   */
  clearKycForm(): void {
    localStorage.removeItem(this.getStorageKey());
  }

  /**
   * 保存KYC表单数据
   */
  saveToLocalStorage({
    formData,
    isIndividual,
    currentStep,
  }: {
    formData?: T;
    isIndividual: boolean;
    currentStep?: EKycStep;
  }): void {
    const storageData = this.loadFromLocalStorage();

    // 更新表单数据
    if (formData) {
      storageData.formData = formData;
    }
    // if (currentStep) {  //因为用户可能回到问卷,然后把问卷的数据删了,这个时候,也需要保存第一步为当前步骤,避免用户刷新之后到之后步骤去
    storageData[isIndividual ? EKycType.INDIVIDUAL : EKycType.INSTITUTION] = currentStep || 0;
    // }

    try {
      const dataStr = JSON.stringify(storageData);
      localStorage.setItem(this.getStorageKey(), dataStr);
    } catch (error) {
      console.error("Failed to save KYC data to storage:", error);
    }
  }
}

// 导出默认实例
export const kycWebStorage = new KycWebStorage();
